import * as THREE from "three";
import { fetchAsset } from "./AssetSource.js";
import { DEFAULT_BACKDROP_COLOR, WHITE_ROOM_ENVIRONMENT } from "../utils/constants.js";

const windowlessWalls = () => ({
  north: { hasWindows: false, windows: [] },
  south: { hasWindows: false, windows: [] },
  east: { hasWindows: false, windows: [] },
  west: { hasWindows: false, windows: [] },
});

/**
 * Default per-environment lighting, merged under each environment's `lighting`
 * block from environments.json. Two modes:
 *  - "sky":    skybox sun + ceiling spotlight cast the shadows (rooms with
 *              walls). `fillLightFactor` scales hemisphere/ambient fill so
 *              shadow depth can be tuned per room.
 *  - "studio": a configurable directional rig (white room / no walls). Each
 *              light can opt into shadow casting via `castShadow` + `shadow`.
 */
// The texture map slots SceneManager._loadMaterialTextures binds, in the order it reads
// them. Kept here because the preload has to stream exactly that set, no more.
const MATERIAL_TEXTURE_KEYS = ['diffuse', 'normal', 'roughness', 'displacement', 'ao', 'arm'];

const DEFAULT_LIGHTING = {
  mode: "sky",
  fillLightFactor: 0.6,
};

const DEFAULT_STUDIO_SHADOW = {
  cameraSize: 7,
  near: 1,
  far: 25,
  mapSize: 2048,
  radius: 4,
  blurSamples: 4,
  normalBias: 0.03,
};

/**
 * Read-only embed mode (?embed=1) presentation, overridable via
 * environments.json `settings.embed`. `shadowQuality: null` leaves the loaded
 * preset's shadow setting untouched.
 */
const DEFAULT_EMBED = {
  environment: WHITE_ROOM_ENVIRONMENT,
  whiteFloor: true,
  showDimensions: false,
  shadowQuality: null,
  // `camera` frames preset embeds; `elementCamera` frames single-element embeds closer.
  camera: { position: [0, 1.6, 8] },
  elementCamera: { position: [2.4, 1.3, 2.6], target: [0, 0.35, 0] },
};

/**
 * EnvironmentManager - Handles environment presets and their application
 *
 * Responsibilities:
 * - Loading environment configuration data
 * - Managing environment presets (standard room, white room, loft, etc.)
 * - Switching between environments
 * - Managing floor/wall materials registry
 * - Coordinating sky, lighting, and visibility changes
 */
export class EnvironmentManager {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;

    // Material registries
    this.floorMaterials = {};
    this.wallMaterials = {};
    this.floorSettings = null;
    this.wallSettings = null;

    // Window and trim configuration
    this.windowConfiguration = null;
    this.wallTrimConfiguration = null;

    // Environment presets
    this.environments = {};
    this.environmentSettings = null;
    this.currentEnvironment = null;
    this.embedSettings = { ...DEFAULT_EMBED };

    // Current selections
    this.currentFloorMaterial = "hardwood";
    this.currentWallMaterial = "drywall";

    // Studio lights for white room
    this.studioLights = null;

    // State management reference
    this.stateManager = null;
  }

  /**
   * Load all environment data from consolidated JSON file
   */
  async loadEnvironmentData() {
    try {
      const response = await fetchAsset("data/environments.json");
      if (!response.ok) {
        throw new Error(`Failed to load environments data: ${response.statusText}`);
      }
      const data = await response.json();

      // Extract floor materials
      this.floorMaterials = data.materials.floors;
      this.floorSettings = {
        textureBasePath: data.settings.textureBasePath.floors
      };

      // Extract wall materials
      this.wallMaterials = data.materials.walls;
      this.wallSettings = {
        textureBasePath: data.settings.textureBasePath.walls
      };

      // Build window configuration from consolidated data
      const defaultEnv = data.environments[data.settings.defaultEnvironment];
      this.windowConfiguration = {
        frameMaterials: data.materials.windowFrames,
        glassMaterials: data.materials.windowGlass,
        windowStyles: data.windowStyles,
        wallConfigurations: this.buildWallConfigurations(defaultEnv)
      };

      // Build wall trim configuration from consolidated data
      this.wallTrimConfiguration = {
        settings: {
          enableBaseboard: defaultEnv?.trim?.baseboard !== false,
          enableCrownMolding: defaultEnv?.trim?.crownMolding !== false,
          defaultMaterial: defaultEnv?.trim?.material || "white_painted"
        },
        materials: data.materials.trim,
        baseboard: data.trimDimensions.baseboard,
        crownMolding: data.trimDimensions.crownMolding
      };

      // Extract environments
      this.environments = data.environments;
      this.environmentSettings = data.settings;
      this.currentEnvironment = data.settings.defaultEnvironment;
      this.embedSettings = { ...DEFAULT_EMBED, ...(data.settings.embed || {}) };

      // Apply default environment settings to floor/wall
      if (defaultEnv) {
        if (defaultEnv.floor && this.floorMaterials[defaultEnv.floor]) {
          this.currentFloorMaterial = defaultEnv.floor;
        }
        if (defaultEnv.wall && this.wallMaterials[defaultEnv.wall]) {
          this.currentWallMaterial = defaultEnv.wall;
        }
      }

      return true;
    } catch (error) {
      console.error("Error loading environments data:", error);
      this._applyFallbackConfiguration();
      return false;
    }
  }

  /**
   * Apply fallback configuration when data loading fails
   */
  _applyFallbackConfiguration() {
    this.floorMaterials = {
      basic: { name: "Basic Floor", properties: { roughness: 0.8, metalness: 0.0 } }
    };
    this.floorSettings = { textureBasePath: "textures/floors/" };

    this.wallMaterials = {
      basic: { name: "Basic Wall", properties: { roughness: 0.9, metalness: 0.0 } }
    };
    this.wallSettings = { textureBasePath: "textures/walls/" };

    this.windowConfiguration = {
      frameMaterials: {},
      glassMaterials: {},
      windowStyles: {},
      wallConfigurations: windowlessWalls()
    };

    this.wallTrimConfiguration = {
      settings: { enableBaseboard: false, enableCrownMolding: false },
      materials: {},
      baseboard: { height: 0.08, depth: 0.01 },
      crownMolding: { height: 0.06, depth: 0.04 }
    };

    this.environments = {
      white_room: {
        id: WHITE_ROOM_ENVIRONMENT,
        name: "White Room",
        floor: "basic",
        wall: null,
        windows: { enabled: false },
        trim: { enabled: false },
        visibility: { walls: false, floor: true, ceiling: false }
      }
    };
    this.environmentSettings = { defaultEnvironment: WHITE_ROOM_ENVIRONMENT };
    this.currentEnvironment = WHITE_ROOM_ENVIRONMENT;
    this.currentFloorMaterial = "basic";
    this.currentWallMaterial = null;
  }

  /**
   * Build wall configurations for windows from environment preset
   * @param {Object} environment - Environment preset object
   * @returns {Object} Wall configurations for window system
   */
  buildWallConfigurations(environment) {
    if (!environment?.windows?.enabled) {
      return windowlessWalls();
    }

    const windowConfig = environment.windows;
    const walls = windowConfig.walls || { north: true, south: false, east: true, west: false };

    const buildWallConfig = (hasWindow) => ({
      hasWindows: hasWindow,
      windows: hasWindow ? [{
        style: windowConfig.style || "industrial",
        frameMaterial: windowConfig.frameMaterial || "black_metal",
        glassMaterial: windowConfig.glassMaterial || "clear_glass"
      }] : []
    });

    return {
      north: buildWallConfig(walls.north),
      south: buildWallConfig(walls.south),
      east: buildWallConfig(walls.east),
      west: buildWallConfig(walls.west)
    };
  }

  /**
   * Change the entire environment preset
   * @param {string} environmentKey - The key of the environment preset
   * @returns {boolean} Success status
   */
  changeEnvironment(environmentKey) {
    const environment = this.environments[environmentKey];
    if (!environment) {
      console.warn(`Environment '${environmentKey}' not found`);
      return false;
    }

    const previousEnvironmentKey = this.currentEnvironment;
    const scene = this.sceneManager.getScene();

    // If scene isn't ready yet, just update the settings
    if (!scene) {
      this._updateEnvironmentSettings(environmentKey, environment);
      return true;
    }

    this.currentEnvironment = environmentKey;

    // Temporarily disable state saving during environment change
    const originalStateManager = this.stateManager;
    this.stateManager = null;

    // Check if we need to rebuild the room
    const needsRoomRebuild = this.shouldRebuildRoom(environment, previousEnvironmentKey);

    if (needsRoomRebuild) {
      this._applyEnvironmentWithRebuild(environment);
    } else {
      this._applyEnvironmentWithoutRebuild(environment);
    }

    // Re-enable state saving
    this.stateManager = originalStateManager;
    this.stateManager?.saveSceneState();

    return true;
  }

  /**
   * Update environment settings without applying to scene
   */
  _updateEnvironmentSettings(environmentKey, environment) {
    this.currentEnvironment = environmentKey;
    this._adoptEnvironmentMaterials(environment);
  }

  // Floor/wall material keys, trim toggles and window layout an environment declares — the
  // state createRoom() reads, so a rebuild must adopt it first.
  _adoptEnvironmentMaterials(environment) {
    if (environment.floor && this.floorMaterials[environment.floor]) {
      this.currentFloorMaterial = environment.floor;
    }
    if (environment.wall && this.wallMaterials[environment.wall]) {
      this.currentWallMaterial = environment.wall;
    }
    if (this.wallTrimConfiguration?.settings) {
      this.wallTrimConfiguration.settings.enableBaseboard = environment.trim?.baseboard !== false;
      this.wallTrimConfiguration.settings.enableCrownMolding = environment.trim?.crownMolding !== false;
    }
    if (this.windowConfiguration) {
      this.windowConfiguration.wallConfigurations = this.buildWallConfigurations(environment);
    }
  }

  /**
   * Apply environment with full room rebuild
   */
  _applyEnvironmentWithRebuild(environment) {
    this._adoptEnvironmentMaterials(environment);

    // Rebuild the room
    this.sceneManager.getRoomBuilder().createRoom();

    // Apply sky and background
    this.applyEnvironmentSky(environment);

    // createRoom() built a fresh ceiling light + contact shadow at their
    // construction defaults (castShadow = false). Re-apply the active shadow
    // quality so the user's setting persists across environment changes
    this.sceneManager.setShadowQuality(this.sceneManager.shadowQuality);
  }

  /**
   * Apply environment without rebuilding (visibility and material changes only)
   */
  _applyEnvironmentWithoutRebuild(environment) {
    const roomBuilder = this.sceneManager.getRoomBuilder();
    const scene = this.sceneManager.getScene();

    // Check if required elements need to be created - if so, fall back to full rebuild
    const needsWalls = environment.visibility?.walls !== false;
    const needsCeiling = environment.visibility?.ceiling !== false;
    const wallsExist = roomBuilder.walls && roomBuilder.walls.length > 0;
    const ceilingExists = roomBuilder.ceiling !== null;

    if ((needsWalls && !wallsExist) || (needsCeiling && !ceilingExists)) {
      // Elements don't exist that should, need full rebuild
      this._applyEnvironmentWithRebuild(environment);
      return;
    }

    // Apply floor visibility and material
    this._applyFloorChanges(environment, roomBuilder, scene);

    // Apply wall visibility and material
    this._applyWallChanges(environment, roomBuilder);

    // Apply ceiling visibility
    this._applyCeilingChanges(environment, roomBuilder);

    // Apply window configuration
    if (environment.windows) {
      this.applyEnvironmentWindows(environment.windows, roomBuilder);
    }

    // Apply trim configuration
    if (environment.trim) {
      this.applyEnvironmentTrim(environment.trim, roomBuilder);
    }

    // Apply sky configuration
    this.applyEnvironmentSky(environment);
  }

  /**
   * Apply floor visibility and material changes
   */
  _applyFloorChanges(environment, roomBuilder, scene) {
    if (environment.visibility?.floor === false) {
      roomBuilder.setFloorVisible(false);
      this._setGridsVisible(scene, false);
    } else {
      if (environment.floor && this.floorMaterials[environment.floor]) {
        this.changeFloorMaterial(environment.floor);
      }
      roomBuilder.setFloorVisible(true);
      this._setGridsVisible(scene, true);
    }
  }

  /**
   * Apply wall visibility and material changes
   */
  _applyWallChanges(environment, roomBuilder) {
    if (environment.visibility?.walls === false) {
      roomBuilder.setWallsVisible(false);
      roomBuilder.setTrimsVisible(false);
    } else {
      roomBuilder.setWallsVisible(true);

      // Show trim elements based on trim config
      roomBuilder.setBaseboardsVisible(environment.trim?.baseboard !== false);
      roomBuilder.setCrownMoldingsVisible(environment.trim?.crownMolding !== false);

      if (environment.wall && this.wallMaterials[environment.wall]) {
        this.changeWallMaterial(environment.wall);
      }
    }
  }

  /**
   * Apply ceiling visibility changes
   */
  _applyCeilingChanges(environment, roomBuilder) {
    roomBuilder.setCeilingVisible(environment.visibility?.ceiling !== false);
  }

  /**
   * Helper to set grid visibility
   */
  _setGridsVisible(scene, visible) {
    scene?.traverse((object) => {
      if (object.userData.isGrid) {
        object.visible = visible;
      }
    });
  }

  /**
   * Determine if environment change requires full room rebuild
   * @param {Object} environment - New environment configuration
   * @param {string} previousEnvironmentKey - Key of the previous environment
   * @returns {boolean} True if room needs to be rebuilt
   */
  shouldRebuildRoom(environment, previousEnvironmentKey) {
    const currentEnv = this.environments[previousEnvironmentKey];
    if (!currentEnv) return true;

    // Check visibility changes
    if (this._hasVisibilityChange(currentEnv, environment, 'walls')) return true;
    if (this._hasVisibilityChange(currentEnv, environment, 'ceiling')) return true;

    // Check window configuration changes
    if (this._hasWindowConfigChange(currentEnv, environment)) return true;

    // Check trim configuration changes
    if (this._hasTrimConfigChange(currentEnv, environment)) return true;

    return false;
  }

  _hasVisibilityChange(currentEnv, newEnv, property) {
    const currentVisible = currentEnv.visibility?.[property] !== false;
    const newVisible = newEnv.visibility?.[property] !== false;
    return currentVisible !== newVisible;
  }

  _hasWindowConfigChange(currentEnv, newEnv) {
    const currentEnabled = currentEnv.windows?.enabled !== false;
    const newEnabled = newEnv.windows?.enabled !== false;
    if (currentEnabled !== newEnabled) return true;

    if (newEnabled && currentEnabled) {
      // Check if window style changed
      if (currentEnv.windows?.style !== newEnv.windows?.style) return true;

      // Check if frame or glass material changed
      if (currentEnv.windows?.frameMaterial !== newEnv.windows?.frameMaterial) return true;
      if (currentEnv.windows?.glassMaterial !== newEnv.windows?.glassMaterial) return true;

      // Check if window wall configuration changed
      const currentWalls = currentEnv.windows?.walls || {};
      const newWalls = newEnv.windows?.walls || {};
      const directions = ['north', 'south', 'east', 'west'];
      for (const dir of directions) {
        if (!!currentWalls[dir] !== !!newWalls[dir]) return true;
      }
    }
    return false;
  }

  _hasTrimConfigChange(currentEnv, newEnv) {
    const currentEnabled = currentEnv.trim?.enabled !== false;
    const newEnabled = newEnv.trim?.enabled !== false;
    if (currentEnabled !== newEnabled) return true;
    if (currentEnv.trim?.baseboard !== newEnv.trim?.baseboard) return true;
    if (currentEnv.trim?.crownMolding !== newEnv.trim?.crownMolding) return true;
    return false;
  }

  /**
   * Apply window configuration from environment
   */
  applyEnvironmentWindows(windowConfig, roomBuilder) {
    if (windowConfig.enabled === false) {
      roomBuilder.setWindowsVisible(false);
    } else {
      roomBuilder.setWindowsVisible(true);
    }
  }

  /**
   * Apply trim configuration from environment
   */
  applyEnvironmentTrim(trimConfig, roomBuilder) {
    roomBuilder.setBaseboardsVisible(trimConfig.baseboard !== false);
    roomBuilder.setCrownMoldingsVisible(trimConfig.crownMolding !== false);
  }

  /**
   * Apply sky configuration from environment
   */
  applyEnvironmentSky(environment) {
    const skyEnabled = environment.sky?.enabled !== false;
    const sceneManager = this.sceneManager;

    // Toggle sky components
    sceneManager.setSkyVisible(skyEnabled);
    sceneManager.setSkyMasked(this.isSkyMasked(environment));

    // Hide door for white room (no walls = no door needed)
    const doorManager = sceneManager.getDoorManager();
    if (doorManager) {
      doorManager.setVisible(environment.visibility?.walls !== false);
    }

    // Apply the per-environment lighting/shadow configuration
    this.applyEnvironmentLighting(environment);

    const background = this.getBackdropColor(environment);
    const scene = sceneManager.getScene();
    if (background !== null && scene) scene.background = new THREE.Color(background);
  }

  /** Flat stand-in for the sky, which the path tracer can't draw. */
  getBackgroundColor(environment = this.environments[this.currentEnvironment]) {
    if (environment?.backgroundColor) return environment.backgroundColor;
    return environment && environment.sky?.enabled !== false ? 0x87ceeb : null;
  }

  /** A sky behind walls shows only through the windows (src/sky/skyMask.js). */
  isSkyMasked(environment = this.environments[this.currentEnvironment]) {
    return !!environment && environment.sky?.enabled !== false && environment.visibility?.walls !== false;
  }

  /** Realtime background: the backdrop around a masked-sky room, else the background colour. */
  getBackdropColor(environment = this.environments[this.currentEnvironment]) {
    if (this.isSkyMasked(environment)) return environment.backdropColor ?? DEFAULT_BACKDROP_COLOR;
    return this.getBackgroundColor(environment);
  }

  /**
   * Resolve and apply an environment's lighting config (mode + shadows + fill).
   * Falls back to legacy behavior (studio when sky disabled) for environments
   * that don't declare a `lighting` block.
   */
  applyEnvironmentLighting(environment) {
    const sceneManager = this.sceneManager;
    const lighting = { ...DEFAULT_LIGHTING, ...(environment.lighting || {}) };
    const mode =
      environment.lighting?.mode ||
      (environment.sky?.enabled === false ? "studio" : "sky");

    if (mode === "studio") {
      this.createStudioLights(lighting);
    } else {
      this.removeStudioLights();
      // Per-room fill control: lower = deeper cast shadows, higher = airier.
      sceneManager.fillLightFactor = lighting.fillLightFactor ?? DEFAULT_LIGHTING.fillLightFactor;
      sceneManager.updateAmbientLighting(sceneManager.timeOfDay);
    }

    // Fixture lights (ceiling spotlight + camera fill) are owned per-environment
    // in environments.json. Applied in both modes; the studio rig still parks the
    // ceiling light, so its intensity here just sets the "on" baseline.
    this._applyFixtureLighting(lighting);
    // No path-tracer hook here: changeEnvironment() ends with saveSceneState(),
    // which invalidates the trace. That rebuild also picks up the new room
    // geometry, which a light-only re-sync would have missed.
  }

  /**
   * Apply per-environment fixture lights from the resolved lighting config:
   * ceiling spotlight intensity (position is fixed — a model sits on it) and the
   * camera fill light's intensity + enabled default.
   */
  _applyFixtureLighting(lighting) {
    // The setter no-ops on a non-finite value, so the config may omit this.
    this.sceneManager.setCeilingLightIntensity(lighting.ceiling?.intensity);
  }

  /**
   * Create the configurable studio lighting rig (e.g. white room). Driven by
   * the resolved `lighting` config so each light's intensity/position/shadow
   * casting can be tuned per environment in environments.json.
   */
  createStudioLights(lighting = {}) {
    if (this.studioLights) return;

    const scene = this.sceneManager.getScene();
    if (!scene) return;

    // Remember the resolved config so shadow quality/type changes can rebuild
    // the rig (see refreshStudioLighting).
    this._activeLighting = lighting;

    const studio = lighting.studio || {};
    const hemiCfg = lighting.hemisphere || {};

    // Neutralize the hemisphere light so its sky tint doesn't color the studio.
    const hemi = this.sceneManager.hemisphereLight;
    if (hemi) {
      this._savedHemiSkyColor = hemi.color.getHex();
      this._savedHemiGroundColor = hemi.groundColor.getHex();
      this._savedHemiIntensity = hemi.intensity;
      hemi.color.set(hemiCfg.skyColor ?? 0xffffff);
      hemi.groundColor.set(hemiCfg.groundColor ?? 0xffffff);
      hemi.intensity = hemiCfg.intensity ?? 0.8;
    }

    // Optional tone-mapping override (white room uses Linear so pure white
    // doesn't get compressed to grey by ACESFilmic).
    const renderer = this.sceneManager.getRenderer();
    if (renderer && studio.toneMapping) {
      this._savedToneMapping = renderer.toneMapping;
      renderer.toneMapping =
        studio.toneMapping === "linear" ? THREE.LinearToneMapping : THREE.ACESFilmicToneMapping;
    }

    // The ceiling spotlight is a room fixture; in a studio the directional rig
    // owns the look, so park the spotlight to avoid washing out the key shadow.
    const ceilingLight = this.sceneManager.getRoomBuilder()?.getCeilingLight();
    if (ceilingLight) {
      this._savedCeilingVisible = ceilingLight.visible;
      ceilingLight.visible = false;
    }

    this.studioLights = new THREE.Group();

    const lightDefs = studio.lights || [
      { role: "key", intensity: 1.0, position: [5, 10, -5], castShadow: false },
      { role: "fill", intensity: 0.6, position: [-3, 8, 5], castShadow: false },
      { role: "back", intensity: 0.4, position: [-8, 6, 0], castShadow: false },
    ];

    for (const def of lightDefs) {
      const light = new THREE.DirectionalLight(def.color ?? 0xffffff, def.intensity ?? 1.0);
      light.name = def.name || `studio_${def.role || "light"}`;
      light.position.set(...(def.position || [0, 10, 0]));
      this._configureStudioLightShadow(light, def);
      this.studioLights.add(light);
      this.studioLights.add(light.target); // ensure target matrix updates
    }

    // Ambient fill for overall softness
    const ambientBoost = new THREE.AmbientLight(0xffffff, studio.ambient ?? 0.5);
    this.studioLights.add(ambientBoost);

    scene.add(this.studioLights);
  }

  /**
   * Rebuild the studio rig with the last config — used when shadow quality or
   * type changes while a studio environment is active so the key light's shadow
   * (on/off + bias) stays in sync.
   */
  refreshStudioLighting() {
    if (!this.studioLights || !this._activeLighting) return;
    const cfg = this._activeLighting;
    this.removeStudioLights();
    this.createStudioLights(cfg);
    // Rig was rebuilt — its shadow maps don't auto-update, so render them next frame.
    this.sceneManager?.requestShadowUpdate?.();
  }

  /**
   * Flag every shadow-casting studio light to re-render its (otherwise static)
   * shadow map. Called from SceneManager.render() while a shadow refresh is due.
   */
  markStudioShadowsNeedUpdate() {
    if (!this.studioLights) return;
    this.studioLights.traverse((o) => {
      if (o.isLight && o.castShadow && o.shadow) o.shadow.needsUpdate = true;
    });
  }

  /**
   * Apply per-light shadow config to a studio directional light. The rig casts
   * real VSM shadows only in the "vsm" quality; "contact" uses the contact-shadow
   * blob instead and "none" disables shadows entirely.
   */
  _configureStudioLightShadow(light, def) {
    const sceneManager = this.sceneManager;
    const wantsShadow = def.castShadow === true && sceneManager.shadowQuality === "vsm";
    light.castShadow = wantsShadow;
    if (!wantsShadow) return;

    const s = { ...DEFAULT_STUDIO_SHADOW, ...(def.shadow || {}) };
    light.shadow.mapSize.set(s.mapSize, s.mapSize);
    light.shadow.camera.left = -s.cameraSize;
    light.shadow.camera.right = s.cameraSize;
    light.shadow.camera.top = s.cameraSize;
    light.shadow.camera.bottom = -s.cameraSize;
    light.shadow.camera.near = s.near;
    light.shadow.camera.far = s.far;
    light.shadow.radius = s.radius;
    light.shadow.blurSamples = s.blurSamples;
    light.shadow.normalBias = s.normalBias;
    // VSM wants bias = 0 and relies on normalBias.
    light.shadow.bias = 0;
    // Static scene: refresh on demand (markStudioShadowsNeedUpdate) instead of every frame.
    light.shadow.autoUpdate = false;
    light.shadow.camera.updateProjectionMatrix();
  }

  /**
   * Remove studio lights when switching back to normal environment
   */
  removeStudioLights() {
    if (!this.studioLights) return;

    const scene = this.sceneManager.getScene();
    if (scene) {
      scene.remove(this.studioLights);
    }

    this.studioLights.traverse((child) => {
      if (child.dispose) child.dispose();
    });
    this.studioLights = null;

    // Restore tone mapping
    const renderer = this.sceneManager.getRenderer();
    if (renderer && this._savedToneMapping !== undefined) {
      renderer.toneMapping = this._savedToneMapping;
      this._savedToneMapping = undefined;
    }

    // Restore the hemisphere light to its original state
    const hemi = this.sceneManager.hemisphereLight;
    if (hemi && this._savedHemiIntensity !== undefined) {
      hemi.color.setHex(this._savedHemiSkyColor);
      hemi.groundColor.setHex(this._savedHemiGroundColor);
      hemi.intensity = this._savedHemiIntensity;
      this._savedHemiSkyColor = undefined;
      this._savedHemiGroundColor = undefined;
      this._savedHemiIntensity = undefined;
    }

    // Restore the ceiling spotlight that studio mode parked
    const ceilingLight = this.sceneManager.getRoomBuilder()?.getCeilingLight();
    if (ceilingLight && this._savedCeilingVisible !== undefined) {
      ceilingLight.visible = this._savedCeilingVisible;
      this._savedCeilingVisible = undefined;
    }
  }

  // ===== FLOOR MATERIAL METHODS =====

  /**
   * Change floor material
   * @param {string} materialKey - The material key to apply
   * @returns {boolean} Success status
   */
  changeFloorMaterial(materialKey) {
    if (!this.floorMaterials[materialKey]) {
      console.warn(`Floor material '${materialKey}' not found`);
      return false;
    }

    this.currentFloorMaterial = materialKey;
    this.sceneManager.getRoomBuilder().updateFloorMaterial(materialKey);
    this.stateManager?.saveSceneState();

    return true;
  }

  /**
   * Change wall material
   * @param {string} materialKey - The material key to apply
   * @returns {boolean} Success status
   */
  changeWallMaterial(materialKey) {
    if (!this.wallMaterials[materialKey]) {
      console.warn(`Wall material '${materialKey}' not found`);
      return false;
    }

    this.currentWallMaterial = materialKey;
    this.sceneManager.getRoomBuilder().updateWallMaterial(materialKey);
    this.stateManager?.saveSceneState();

    return true;
  }

  // ===== GETTERS =====

  getEnvironments() {
    return this.environments;
  }

  getCurrentEnvironment() {
    return this.currentEnvironment;
  }

  isWhiteRoom() {
    return this.currentEnvironment === WHITE_ROOM_ENVIRONMENT;
  }

  getEmbedSettings() {
    return this.embedSettings;
  }

  getFloorMaterials() {
    return this.floorMaterials;
  }

  getWallMaterials() {
    return this.wallMaterials;
  }

  // ===== STATE MANAGEMENT =====

  /**
   * Get current environment state as serializable object
   */
  getEnvironmentState() {
    return {
      environment: this.currentEnvironment,
    };
  }

  /**
   * Apply environment state from object
   */
  setEnvironmentState(state) {
    if (state.environment && this.environments[state.environment]) {
      this.changeEnvironment(state.environment);
    }
  }

  setStateManager(stateManager) {
    this.stateManager = stateManager;
  }

  dispose() {
    this.removeStudioLights();
  }

  /**
   * Every texture file a floor/wall material references, as logical asset paths.
   *
   * Room materials are built synchronously (SceneManager.createMaterial is reached from
   * eight sync call sites in RoomBuilder), so they cannot stream on demand the way models
   * and fabrics do. Callers ensure these up front at the one async seam above the
   * synchronous chain — boot, and the environment picker.
   */
  materialAssetPaths(materialData, isWall) {
    const dir = this.materialTextureDir(materialData, isWall);
    if (!dir) return [];
    // Only the maps the loader actually binds: `displacement` is wall-only, so streaming
    // it for a floor downloads a 1k map that is never used (three shipped floors have one).
    return MATERIAL_TEXTURE_KEYS.filter((key) => key !== 'displacement' || isWall)
      .map((key) => materialData.textures[key])
      .filter(Boolean)
      .map((file) => dir + file);
  }

  /**
   * Directory the material's texture files live in. Shared with
   * `SceneManager._loadMaterialTextures`, which builds the same path to load them — the
   * two must agree or the preload streams files the loader never asks for.
   */
  materialTextureDir(materialData, isWall) {
    if (!materialData?.textures || !materialData?.textureFolder) return null;
    const basePath = isWall ? this.wallSettings?.textureBasePath : this.floorSettings?.textureBasePath;
    if (!basePath) return null;
    return `${basePath}${materialData.textureFolder}/`;
  }

  /** Asset paths for an environment's floor + wall, or the current one. */
  environmentAssetPaths(envKey = this.currentEnvironment) {
    const env = this.environments?.[envKey];
    if (!env) return [];
    const paths = [];
    const floor = this.floorMaterials?.[env.floor];
    const wall = this.wallMaterials?.[env.wall];
    if (floor) paths.push(...this.materialAssetPaths(floor, false));
    if (wall) paths.push(...this.materialAssetPaths(wall, true));
    return paths;
  }

}
